import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { storageGet, storageSet } from '../services/storage';

export type CurrencyDisplayMode = 'reporting' | 'original';

const STORAGE_KEY = 'econori.currencyDisplay';

interface CurrencyDisplayContextValue {
  mode: CurrencyDisplayMode;
  setMode: (mode: CurrencyDisplayMode) => void;
}

const CurrencyDisplayContext = createContext<CurrencyDisplayContextValue | undefined>(undefined);

export const CurrencyDisplayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<CurrencyDisplayMode>(() => {
    const stored = storageGet(STORAGE_KEY);
    return stored === 'original' ? 'original' : 'reporting';
  });

  const setMode = useCallback((next: CurrencyDisplayMode) => {
    setModeState(next);
    storageSet(STORAGE_KEY, next);
  }, []);

  // Keep multiple tabs in sync — listen for cross-tab storage events.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'original' || e.newValue === 'reporting')) {
        setModeState(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <CurrencyDisplayContext.Provider value={{ mode, setMode }}>
      {children}
    </CurrencyDisplayContext.Provider>
  );
};

export function useCurrencyDisplay(): CurrencyDisplayContextValue {
  const ctx = useContext(CurrencyDisplayContext);
  if (!ctx) {
    // Allow components to be rendered outside the provider in tests by returning a sensible default.
    return {
      mode: 'reporting',
      setMode: () => {},
    };
  }
  return ctx;
}
